/**
 * Roster duplicate / collision warnings for bulk celebration periods.
 * See docs/bulk-celebrations-playbook.md §5–§7.
 */

import { normalizePersonName } from "./bulkCelebrations.ts";

export type BulkRosterWarningSeverity = "info" | "warn" | "block";

export type BulkRosterWarning = {
  code: string;
  severity: BulkRosterWarningSeverity;
  message: string;
};

export type RosterMemberInput = {
  team_member_id: string;
  first_name: string;
  last_name: string;
  birthday?: string | null;
  milestone_date?: string | null;
  occasion_type?: string;
};

export type ExistingRosterMember = RosterMemberInput & {
  celebration_id?: string;
};

function sameDate(a?: string | null, b?: string | null): boolean {
  if (!a || !b) return false;
  return a.slice(0, 10) === b.slice(0, 10);
}

export function evaluateBulkRosterAdd(
  candidate: RosterMemberInput,
  existing: ExistingRosterMember[],
): BulkRosterWarning[] {
  const warnings: BulkRosterWarning[] = [];
  const candName = normalizePersonName(candidate.first_name, candidate.last_name);

  const nameMatches = existing.filter(
    (m) => normalizePersonName(m.first_name, m.last_name) === candName,
  );

  if (nameMatches.length > 0) {
    const birthdayCollision = nameMatches.some((m) =>
      sameDate(m.birthday, candidate.birthday),
    );
    const milestoneCollision = nameMatches.some((m) =>
      sameDate(m.milestone_date, candidate.milestone_date),
    );

    if (birthdayCollision || milestoneCollision) {
      warnings.push({
        code: "E3",
        severity: "block",
        message:
          "Someone with this name and the same birthday or milestone date is already on the roster.",
      });
      return warnings;
    }

    warnings.push({
      code: "E1",
      severity: "warn",
      message:
        "Another person with a similar name is already on this period's roster. Confirm this is a different teammate.",
    });
  }

  const occasion = candidate.occasion_type ?? "";
  if (occasion === "birthday" && candidate.birthday) {
    const dupBirthday = existing.some(
      (m) =>
        m.team_member_id !== candidate.team_member_id &&
        sameDate(m.birthday, candidate.birthday),
    );
    if (dupBirthday) {
      warnings.push({
        code: "R1",
        severity: "warn",
        message: "Another roster member shares this birthday date.",
      });
    }
  }

  if (occasion === "work_anniversary" && candidate.milestone_date) {
    const dupAnn = existing.some(
      (m) =>
        m.team_member_id !== candidate.team_member_id &&
        sameDate(m.milestone_date, candidate.milestone_date),
    );
    if (dupAnn) {
      warnings.push({
        code: "R2",
        severity: "warn",
        message: "Another roster member shares this work anniversary date.",
      });
    }
  }

  if ((occasion === "welcome" || occasion === "farewell") && nameMatches.length > 0) {
    warnings.push({
      code: "R4",
      severity: "warn",
      message: `A ${occasion} for someone with this name may already be in this period.`,
    });
  }

  return warnings;
}

export function hasBlockingRosterWarning(warnings: BulkRosterWarning[]): boolean {
  return warnings.some((w) => w.severity === "block");
}

/** R2: same celebration already on this period's roster. */
export function celebrationAlreadyOnRoster(
  celebrationId: string,
  rosterCelebrationIds: string[],
): boolean {
  return rosterCelebrationIds.includes(celebrationId);
}
